import { StoryDebugData } from '../../context/ChatContext'
import styles from './StoryDebugSidebar.module.css'

interface Props {
  data: StoryDebugData | null
  pending: boolean
  /** Omitted when the sidebar renders inline rather than as a dismissible
   *  overlay, in which case no close button is drawn. */
  onClose?: () => void
}

export default function StoryDebugSidebar({ data, pending, onClose }: Props) {
  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <span>Story Debug</span>
        {onClose ? <button className={styles.closeBtn} onClick={onClose}>×</button> : null}
      </div>
      {pending && <div className={styles.loadingBar} />}
      <div className={styles.content}>
        {data ? (
          <>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Phase</h4>
              <div className={styles.phaseId}>{data.phase_id || '--'}</div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Turn: </span>
                <span>{data.turn_count ?? 0}/{data.max_turns && data.max_turns > 0 ? data.max_turns : 'unlimited'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.label}>Tone: </span>
                <span>{data.tone || '--'}</span>
              </div>
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Prefabs</h4>
              <ul className={styles.list}>
                {data.prefabs && data.prefabs.length > 0 ? data.prefabs.map(t => (
                  <li key={t.id} className={`${styles.item} ${styles.prefab}`}>
                    <span className={styles.itemId}>{t.id}</span>
                    <span className={styles.itemDesc}>{t.condition}</span>
                    <span className={styles.itemMeta}>goto: {t.goto}</span>
                  </li>
                )) : <li className={styles.empty}>No prefabs</li>}
              </ul>
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Objectives</h4>
              <ul className={styles.list}>
                {data.objectives && data.objectives.length > 0 ? data.objectives.map(obj => (
                  <li key={obj.id} className={[
                    styles.item,
                    obj.completed ? styles.completed : '',
                    obj.effectiveness === 'advanced' ? styles.effAdvanced : '',
                    obj.effectiveness === 'partially_addressed' ? styles.effPartial : ''
                  ].filter(Boolean).join(' ')}>
                    <span className={styles.itemId}>
                      {obj.id}
                      {obj.completed && ' [done]'}
                      {obj.effectiveness === 'advanced' && <span className={`${styles.effBadge} ${styles.badgeAdvanced}`}>advanced</span>}
                      {obj.effectiveness === 'partially_addressed' && <span className={`${styles.effBadge} ${styles.badgePartial}`}>partial</span>}
                      {obj.effectiveness === 'not_addressed' && <span className={`${styles.effBadge} ${styles.badgeNot}`}>not addressed</span>}
                    </span>
                    <span className={styles.itemDesc}>{obj.description}</span>
                    {obj.urgency && <span className={`${styles.itemMeta} ${styles['urgency_' + obj.urgency?.toLowerCase()]}`}>urgency: {obj.urgency}</span>}
                    {obj.steering && <span className={styles.steering}>{obj.steering}</span>}
                  </li>
                )) : <li className={styles.empty}>No objectives</li>}
              </ul>
            </div>

            {data.player_state && Object.keys(data.player_state).length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Player Memory</h4>
                <ul className={styles.list}>
                  {Object.entries(data.player_state).map(([key, value]) => (
                    <li key={key} className={styles.item}>
                      <strong>{key.replace('player.', '').replace(/_/g, ' ')}:</strong> {value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Last Reasoning</h4>
              <pre className={styles.reasoning}>{data.reasoning || '--'}</pre>
            </div>
          </>
        ) : (
          <div className={styles.empty}>No story data yet</div>
        )}
      </div>
    </aside>
  )
}
